// @flow
import React from 'react'
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel'
import Radio from '@material-ui/core/Radio/Radio'
import Slider from 'rc-slider'
import { sliderStyle } from '../styles/ficus_map_style'
import themeStyles from '../styles'
import type { AnalyzeFrameworkBarType } from '../reducers/analyze_framework_bar'
import type { SelectFrameworkBarType } from '../reducers/select_framework_bar'
import * as colors from '../styles/colors'

type Props = {
    config: AnalyzeFrameworkBarType,
    ficus: SelectFrameworkBarType,
    setConfig: Function,
    theme: themeStyles,
}

class AnimationControls extends React.Component<Props> {
    speedSettings: [number, number, number] = [2000, 1000, 500]
    speedDisplaySettings: [string, string, string] = ['1x', '2x', '4x']

    render() {
        const ficusData = this.props.ficus.ficusData

        // Check for rasters or transims. Rasters have an interval for every theme
        //   while transims have a transim object for theme.
        const hasRasters = ficusData.themes.length
        const nframes = hasRasters ? ficusData.themes[0].mapfiles.length : ficusData.themes.transims.length

        const { started, iInterval } = this.props.config
        const ispeed = this.speedSettings.indexOf(this.props.config.speedFrame)
        const disableAnimationControls = this.props.config.mode === '_ranges'

        // Build frame labels
        const frameNames = {}
        const getFrameName = (iframe: number) => {
            const order = this.props.config.mode === '_allRealizations' ? this.props.config.iterationOrder : []
            const i = order.length ? order[iframe] : iframe
            let ret = { label: '' }
            // Check for conventional rasters
            if (hasRasters) {
                const m = ficusData.themes[iInterval].mapfiles[i]
                if (m) {
                    const frameName = m.layer
                    const s1 = frameName.lastIndexOf('_')
                    const s2 = frameName.lastIndexOf('.')
                    ret = frameName.substring(s1 + 1, s2)
                    if (ret.indexOf('rzn') === 0) {
                        // Also trim off the rzn part of the filename
                        ret = ret.substr(3)
                    }
                    ret = { label: ret, style: { fontSize: '14px' } }
                }
            } else {
                // Transims
                if (ficusData.themes.transims[iframe]) {
                    const filepath = ficusData.themes.transims[iframe].filepath
                    // Realization is the parent of the transim shapefile
                    const reRealization = /.*\/_.+\/.+_(.+)\/.*/
                    // Just match the number at the end
                    const match = filepath.match(reRealization)
                    let realization = match[1]
                    if (realization.indexOf('rzn') === 0) {
                        // Also trim off the rzn part of the filename
                        realization = realization.substr(3)
                    }
                    ret = { label: realization, style: { fontSize: '14px' } }
                }
            }
            return ret
        }

        // Currently transims (vector) themes only have _allRealizations mode
        if (this.props.config.mode === '_allRealizations') {
            if (nframes) {
                if (this.props.config.iterationType === 'grayswan') {
                    frameNames[0] = {
                        style: {
                            marginLeft: '-30%',
                        },
                        label: <span>Most Gray Swannish</span>,
                    }
                    frameNames[nframes - 1] = {
                        style: {
                            marginLeft: '-60%',
                        },
                        label: <span>Most White Swannish</span>,
                    }
                } else {
                    frameNames[0] = 1
                    frameNames[nframes - 1] = nframes
                }
            }
        } else if (hasRasters) {
            ficusData.themes[this.props.config.iInterval].mapfiles.forEach((m, i) => {
                frameNames[i] = getFrameName(i)
            })
        } else if (nframes) {
            ficusData.themes.transims.forEach((m, i) => {
                frameNames[i] = getFrameName(i)
            })
        }

        let intervals = ficusData.intervals

        // Remove common base
        function sharedStart(A) {
            const a1 = A[0]
            const a2 = A[A.length - 1]
            const L = a1.length
            let i = 0
            while (i < L && a1.charAt(i) === a2.charAt(i)) i += 1
            return a1.substring(0, i)
        }
        const base = intervals.length ? sharedStart(intervals) : ''
        intervals = intervals.map(i => `${i.slice(0, i.length - 2)}:${i.slice(i.length - 2)}`.substr(base.length))

        const intervalNames = {}
        if (intervals.length) {
            intervalNames[0] = { label: [intervals[0]], style: { fontSize: '14px' } }
            intervalNames[intervals.length - 1] = { label: [intervals[intervals.length - 1]], style: { fontSize: '14px' } }
        }

        const onClickStop = () => {
            if (!disableAnimationControls) {
                this.props.setConfig({
                    started: !started,
                })
            }
        }

        function mod(n, m) {
            return ((n % m) + m) % m
        }
        const onClickPrev = () => {
            if (this.props.config.animationType === 'frame') {
                this.props.setConfig({
                    iframe: mod(this.props.config.iframe - 1, nframes),
                    started: false,
                })
            } else if (this.props.config.animationType === 'interval') {
                this.props.setConfig({
                    iInterval: mod(this.props.config.iInterval - 1, intervals.length),
                    started: false,
                })
            }
        }
        const onClickNext = () => {
            if (this.props.config.animationType === 'frame') {
                this.props.setConfig({
                    iframe: mod(this.props.config.iframe + 1, nframes),
                    started: false,
                })
            } else if (this.props.config.animationType === 'interval') {
                this.props.setConfig({
                    iInterval: mod(this.props.config.iInterval + 1, intervals.length),
                    started: false,
                })
            }
        }

        const onClickChangeSpeed = () => {
            // Ranges are not allowed to be animated
            if (!disableAnimationControls) {
                if (started) {
                    const newispeed = (ispeed + 1) % this.speedSettings.length
                    const currentSpeed = this.speedSettings[newispeed]
                    this.props.setConfig({
                        speedFrame: currentSpeed,
                        speedTransition: currentSpeed / 5,
                        started: true,
                    })
                }
            }
        }

        const onChangeFrame = (iframe) => {
            this.props.setConfig({ iframe, started: false })
        }

        const onChangeInterval = (currentInterval) => {
            this.props.setConfig({ iInterval: currentInterval, started: false })
        }

        const onChangeAnimationPlaybackType = (e) => {
            this.props.setConfig({ animationType: e.target.value, started: false })
        }

        const intervalRadioStyle = {
            color: colors.lightgray,
        }

        const speedContainerStyles = [this.props.theme.speedContainer]
        if (disableAnimationControls) speedContainerStyles.push(this.props.theme.disabled)

        const styles = {
            label: {
                display: 'inline-block',
                width: '80px',
            },
            value: {
                display: 'inline-block',
                float: 'right',
                marginRight: '20px',
                textDecoration: 'underline',
            },
        }

        return (
            <div>
                <div className={this.props.theme.ficusControlsHeader}>
                    <span className={this.props.theme.ficusControlsHeaderTitle}>Animation</span>
                </div>
                <span className={this.props.theme.speedContainer}>
                    <button
                        className={this.props.theme.toolbutton}
                        onClick={onClickPrev}
                        title="Click to go to previous frame"
                    >
                        <i className={'fa fa-step-backward'} />
                    </button>
                    <button
                        className={this.props.theme.toolbutton}
                        onClick={onClickNext}
                        title="Click to go to next frame"
                    >
                        <i className={'fa fa-step-forward'} />
                    </button>
                </span>
                <div className={speedContainerStyles.join(' ')} style={{ display: 'inline-block' }}>
                    <button
                        className={this.props.theme.toolbutton}
                        onClick={onClickStop}
                        title="Click to pause animation"
                    >
                        <i className={started ? 'fa fa-pause' : 'fa fa-play'} />
                    </button>
                    <button
                        className={this.props.theme.toolbutton}
                        style={{ opacity: started ? 1 : 0.5 }}
                        onClick={onClickChangeSpeed}
                        title="Click to start animation or alter animation speed"
                    >
                        <i className={'fa fa-forward'} />
                    </button>
                    <span className={this.props.theme.speedLabel}>
                        {started ? this.speedDisplaySettings[ispeed] : 'Paused'}
                    </span>
                    {this.props.config.mode === '_allRealizations' ? (
                        <span className={this.props.theme.frameLabel}>
                            {getFrameName(this.props.config.iframe).label}
                        </span>
                    ) : undefined}
                </div>

                <table style={{ marginTop: '20px', width: '100%' }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '10px' }}>&nbsp;</td>
                            <td style={{ maxWidth: '20px', overflow: 'hidden' }}>
                                {intervals.length > 1 && (
                                    <FormControlLabel
                                        checked={this.props.config.animationType === 'frame'}
                                        control={<Radio style={intervalRadioStyle} />}
                                        label=""
                                        name="frame"
                                        onChange={onChangeAnimationPlaybackType}
                                        value="frame"
                                    />
                                )}
                            </td>
                            <td>
                                <span style={styles.label}>Realizations</span>
                                <span style={styles.value}>{getFrameName(this.props.config.iframe).label}</span>
                            </td>
                        </tr><tr>
                            <td colSpan={3} style={{ width: '100%' }}>
                                <div className={this.props.theme.sliderContainer}>
                                    <Slider
                                        {...sliderStyle}
                                        className={this.props.theme.slider}
                                        min={0}
                                        max={nframes - 1}
                                        marks={frameNames}
                                        value={this.props.config.iframe}
                                        onChange={onChangeFrame}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {intervals.length > 1 && (
                    <table style={{ width: '100%' }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '10px' }}>&nbsp;</td>
                                <td style={{ maxWidth: '20px', overflow: 'hidden' }}>
                                    <div className={this.props.theme.radioContainer}>
                                        <FormControlLabel
                                            checked={this.props.config.animationType === 'interval'}
                                            control={<Radio style={intervalRadioStyle} />}
                                            label=""
                                            name="interval"
                                            onChange={onChangeAnimationPlaybackType}
                                            value="interval"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <span style={styles.label}>Time</span>
                                    <span style={styles.value}>{intervals[this.props.config.iInterval]}</span>
                                </td>
                            </tr><tr>
                                <td colSpan={3} style={{ width: '100%' }}>
                                    <span style={{ display: 'none' }}>{intervals[this.props.config.iInterval]}</span>
                                    <div className={this.props.theme.sliderContainer}>
                                        <Slider
                                            {...sliderStyle}
                                            className={this.props.theme.slider}
                                            min={0}
                                            marks={intervalNames}
                                            max={intervals.length - 1}
                                            value={this.props.config.iInterval}
                                            onChange={onChangeInterval}
                                        />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>
        )
    }
}

export default AnimationControls

